import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { Plus } from 'lucide-react';
import { FilterResetButton } from './FilterResetButton';
import { useStartingUniverseStore } from '../../store/useStartingUniverseStore';
import { PillInput } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/components/PillInput';

const DEBOUNCE_MS = 150;

// "No cap": the max is uncapped ONLY at this sentinel (a cleared or defaulted
// bound), NEVER merely because it sits at/above the current data ceiling. The
// histograms are point-in-time — a floor or cap may legitimately fall outside
// today's range (the chart even trims the top 1%), so any finite value the user
// types is kept as a real bound.
const UNCAPPED = Number.MAX_SAFE_INTEGER;

interface DistributionData {
  bins: Array<number>;
  min: number;
  max: number;
}

interface HistogramRangeFilterProps {
  label: string;
  /**
   * Names THIS slider removes from the scoped population, independently of its
   * siblings. Rendered next to the label so each filter carries its own cost,
   * the way the sections and the card above it already do.
   */
  excludedCount?: number;
  distribution: Array<number>;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatAxis: (v: number) => string;
  axisTicks?: number;
  minLabel: string;
  maxLabel: string;
  formatInput: (v: number) => string;
  parseInput?: (s: string) => number | null;
  maxPlaceholder?: string;
  isActive?: boolean;
  logDistribution?: DistributionData;
  /** Range applied when the user opens the filter via "Set Range" (the
   * product default, e.g. a minimum threshold). Falls back to the current
   * value when omitted. */
  defaultRange?: [number, number];
  /** When false, the Max field is disabled/"not available" (e.g. Listing
   * History only takes a minimum). Defaults to true. */
  maxAvailable?: boolean;
  /**
   * True data ceiling, when `max` is a TRIMMED chart ceiling (the price
   * histogram drops its top 1% so the body of the distribution is legible).
   * The chart and the slider live in the trimmed space; "is this filter
   * capped?" is still judged against the real one, so a value above the chart
   * still reads as its own number rather than collapsing to "no cap".
   */
  fullMax?: number;
  /** Initial scale when the Lin/Log toggle is available. Defaults to log
   * (distributions here are right-skewed); pass false for near-uniform fields
   * like Float % that read better linearly. */
  defaultLog?: boolean;
  /** Value the "+ Max" button seeds on click — the 90th percentile, so one
   * click caps off the top ~10% of extreme-high values. Omit to keep the old
   * behaviour (opens an empty max field). */
  maxDefault?: number;
  /** A field's PHYSICAL ceiling — e.g. 100 for Float %, which can't exceed
   * 100%. Typed bounds clamp to it (a TYPE rule, like History being integer).
   * Omit for open-ended fields (price / ADV / market cap) whose values are any
   * positive number across history. */
  valueMax?: number;
}

const HistogramBars = React.memo(function HistogramBars({
  distribution,
  min,
  max,
  rangeMin,
  rangeMax,
  isLog,
}: {
  distribution: Array<number>;
  min: number;
  max: number;
  rangeMin: number;
  rangeMax: number;
  isLog?: boolean;
}) {
  const maxVal = useMemo(() => Math.max(...distribution, 1), [distribution]);

  const n = distribution.length;

  // Fractional bounds of the selection, in the same space the bars are drawn
  // in. Both axes now agree: the slider follows the scale, so log mode simply
  // measures the fractions in log space instead of projecting them onto a
  // linear track.
  const [minFrac, maxFrac] = useMemo(() => {
    if (isLog && min > 0 && max > min) {
      const lgLo = Math.log10(min);
      const span = Math.log10(max) - lgLo;
      const frac = (v: number) =>
        (Math.log10(Math.min(Math.max(v, min), max)) - lgLo) / span;
      return [frac(rangeMin), frac(rangeMax)];
    }
    if (max <= min) return [0, 1];
    return [(rangeMin - min) / (max - min), (rangeMax - min) / (max - min)];
  }, [isLog, rangeMin, rangeMax, min, max]);

  return (
    <div className="relative flex items-end h-20 mb-3 gap-px rounded-lg overflow-hidden bg-gradient-to-b from-muted/5 via-muted/10 to-muted/15 p-1.5 shadow-inner">
      {/* The kept span, washed behind the bars. Without it the histogram and
          the slider below read as two separate controls; this ties the two
          together and makes a narrow selection visible even where the
          distribution is sparse. */}
      <div
        aria-hidden
        className="absolute inset-y-0 bg-primary/[0.07] border-x border-primary/20 pointer-events-none transition-all duration-300 ease-out"
        style={{
          left: `${Math.max(0, Math.min(1, minFrac)) * 100}%`,
          right: `${(1 - Math.max(0, Math.min(1, maxFrac))) * 100}%`,
        }}
      />
      {distribution.map((val, i) => {
        const inRange = (i + 1) / n > minFrac && i / n < maxFrac;
        const height = Math.max((val / maxVal) * 100, 3);

        // Follows the Figma's purple bars (UniverseSelection.tsx:3854) for the
        // KEPT range. Excluded bars deliberately leave the Figma's faded-purple
        // behind: at 0.35 opacity they still read as the same colour, so the
        // eye had to work out where the selection ended. Neutral grey makes the
        // boundary obvious at a glance, which is the whole job of this chart.
        return (
          <div
            key={i}
            className="relative flex-1 rounded-t-sm transition-all duration-300 ease-out"
            style={{
              height: `${height}%`,
              background: inRange
                ? 'linear-gradient(to top, #7c3aed, #8b5cf6)'
                : 'rgba(120, 120, 135, 0.28)',
              boxShadow: inRange
                ? '0 -1px 2px rgba(124, 58, 237, 0.15)'
                : 'none',
            }}
          />
        );
      })}
    </div>
  );
});

const AxisLabels = React.memo(function AxisLabels({
  min,
  max,
  ticks,
  formatAxis,
  isLog,
}: {
  min: number;
  max: number;
  ticks: number;
  formatAxis: (v: number) => string;
  isLog?: boolean;
}) {
  const labels = useMemo(() => {
    if (isLog && min > 0 && max > 0) {
      const logMin = Math.log10(min);
      const logMax = Math.log10(max);
      const logStep = (logMax - logMin) / (ticks - 1);
      return Array.from({ length: ticks }, (_, i) => {
        const raw = Math.pow(10, logMin + i * logStep);
        if (raw >= 1000) return Math.round(raw);
        if (raw >= 1) return Math.round(raw * 100) / 100;
        return Math.round(raw * 10000) / 10000;
      });
    }
    const step = (max - min) / (ticks - 1);
    return Array.from({ length: ticks }, (_, i) => min + i * step);
  }, [min, max, ticks, isLog]);

  return (
    <div className="relative mb-2 px-0.5">
      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-[1px] bg-border/60" />
      <div className="relative flex justify-between items-center text-[11px] text-muted-foreground/70 font-medium tracking-tight tabular-nums">
        {labels.map((v, i) => (
          // A tick sitting on the container edge would hang past the chart, so
          // the first and last are pulled inward instead of centred.
          <span key={i} className="px-1 first:pl-0 last:pr-0">
            {formatAxis(v)}
          </span>
        ))}
      </div>
    </div>
  );
});

// Positions the log slider resolves to. Fine enough that a drag feels
// continuous without emitting a new value on every pixel.
const LOG_SLIDER_STEPS = 1000;

const DualSlider = React.memo(function DualSlider({
  min,
  max,
  step,
  value,
  onChange,
  logBounds,
}: {
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  /**
   * When the chart is drawn on a log axis, the slider has to follow it. On a
   * linear track the entire body of a log-normal distribution — every name
   * between $1 and $500 — was crammed into the first pixels, so the useful
   * range was undraggable while the empty tail took the whole track.
   */
  logBounds?: [number, number];
}) {
  if (logBounds && logBounds[0] > 0 && logBounds[1] > logBounds[0]) {
    const lgLo = Math.log10(logBounds[0]);
    const span = Math.log10(logBounds[1]) - lgLo;
    const toPos = (v: number) =>
      Math.round(
        ((Math.log10(Math.min(Math.max(v, logBounds[0]), logBounds[1])) -
          lgLo) /
          span) *
          LOG_SLIDER_STEPS,
      );
    const toVal = (p: number) =>
      Math.pow(10, lgLo + (p / LOG_SLIDER_STEPS) * span);
    return (
      <SliderPrimitive.Root
        min={0}
        max={LOG_SLIDER_STEPS}
        step={1}
        value={[toPos(value[0]), toPos(value[1])]}
        onValueChange={(v) =>
          onChange([toVal(v[0] as number), toVal(v[1] as number)])
        }
        className="relative flex w-full touch-none items-center select-none h-5"
      >
        <SliderPrimitive.Track className="bg-border/40 relative grow overflow-hidden rounded-full h-[2px] w-full">
          <SliderPrimitive.Range className="bg-primary absolute h-full" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="block size-4 shrink-0 rounded-full border-2 border-background bg-primary shadow-md transition-transform hover:scale-110 focus-visible:outline-hidden" />
        <SliderPrimitive.Thumb className="block size-4 shrink-0 rounded-full border-2 border-background bg-primary shadow-md transition-transform hover:scale-110 focus-visible:outline-hidden" />
      </SliderPrimitive.Root>
    );
  }
  return (
    <SliderPrimitive.Root
      min={min}
      max={max}
      step={step}
      value={value}
      onValueChange={(v) => onChange(v as [number, number])}
      className="relative flex w-full touch-none items-center select-none h-5"
    >
      <SliderPrimitive.Track className="bg-border/40 relative grow overflow-hidden rounded-full h-[2px] w-full">
        <SliderPrimitive.Range className="bg-primary absolute h-full" />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb className="block size-4 shrink-0 rounded-full border-2 border-background bg-primary shadow-md transition-transform hover:scale-110 focus-visible:outline-hidden" />
      <SliderPrimitive.Thumb className="block size-4 shrink-0 rounded-full border-2 border-background bg-primary shadow-md transition-transform hover:scale-110 focus-visible:outline-hidden" />
    </SliderPrimitive.Root>
  );
});

// --- Main Component ---

export function HistogramRangeFilter({
  label,
  excludedCount = 0,
  distribution,
  min,
  max,
  step,
  value,
  onChange,
  formatAxis,
  axisTicks = 5,
  minLabel,
  maxLabel,
  formatInput,
  parseInput,
  maxPlaceholder = '+ Max',
  isActive,
  logDistribution,
  defaultRange,
  maxAvailable = true,
  fullMax,
  defaultLog = true,
  maxDefault,
  valueMax,
}: HistogramRangeFilterProps) {
  // Everything about "is this restricting anything" is judged against the real
  // data ceiling; only the drawing uses the trimmed one.
  const dataMax = fullMax ?? max;
  // "+ Min" restores the product-default floor (the low end of defaultRange) in
  // one click — the mirror of "+ Max" seeding the p90 cap — so a cleared min
  // can be put straight back. Undefined ⇒ "+ Min" opens an empty field instead.
  const minSeed = defaultRange?.[0];
  // "+ Max" only SEEDS the p90 cap when that cap sits strictly below the data
  // ceiling. Otherwise the seed equals "no cap" and the button is a dead no-op
  // — exactly the Float % case, where p90 ≈ 100% IS the ceiling, so clicking
  // "+ Max" appeared to do nothing and never let a cap be typed. When the seed
  // isn't meaningful, `maxSeed` is undefined and "+ Max" opens an empty editor
  // instead (see PillInput.handleFocus), so a cap can still be entered by hand.
  const maxSeed =
    maxDefault != null && maxDefault < dataMax ? maxDefault : undefined;
  // A filter is "active" when its value is a narrowed range. This uses the SAME
  // test as `minIsSet` / `isAtMax` below (a min that isn't the cleared floor; a
  // max below the ceiling) so the three never disagree. Earlier this read
  // `value[0] > min`, which a default floor sitting AT/BELOW the chart's own
  // minimum (e.g. a $5 price floor on a universe that starts higher) failed —
  // even though the field still shows it — and that split is what let clearing
  // the MAX close a card whose min still stood. Start EXPANDED whenever active
  // so a hydrated value (e.g. a Soteria-built strategy's $10B market-cap floor)
  // is shown, not hidden behind "Set Range". An explicit `isActive` prop wins.
  const valueActive =
    (value[0] > 0 && value[0] !== min) ||
    (maxAvailable && value[1] < UNCAPPED);

  // Open state is remembered in the store, keyed by the filter's label.
  // FilterSection unmounts its children when it collapses, so component-local
  // state alone meant a histogram re-derived its openness from whether its
  // value was still narrowed — and a filter the user had deliberately CLEARED
  // came back as an unconfigured "+ Set Range", indistinguishable from one
  // never touched. Falls back to the value-derived default until the user
  // opens or closes it themselves.
  const storedExpanded = useStartingUniverseStore(
    (st) => st.expandedHistogramIds[label],
  );
  const setStoredExpanded = useStartingUniverseStore(
    (st) => st.setHistogramExpanded,
  );
  const expanded = storedExpanded ?? isActive ?? valueActive;
  const setExpanded = (next: boolean) => setStoredExpanded(label, next);
  // Default to log scale — distributions here are heavily right-skewed
  // (price/ADV/market cap), so log reads better out of the box. Falls back to
  // linear when the filter has no usable log distribution (hasLogData=false).
  const [isLogScale, setIsLogScale] = useState(defaultLog);

  // Show the Lin/Log toggle whenever the filter has a usable (positive) log
  // distribution. Previously gated on a ≥100× span, which hid the toggle on the
  // narrower filters (history, float); they now get the same toggle as
  // liquidity / market cap.
  const hasLogData =
    logDistribution != null &&
    logDistribution.bins.length > 0 &&
    logDistribution.min > 0 &&
    logDistribution.max > logDistribution.min;
  const showLog = isLogScale && hasLogData;

  const activeDist = showLog ? logDistribution.bins : distribution;
  const activeMin = showLog ? logDistribution.min : min;
  const activeMax = showLog ? logDistribution.max : max;

  // Local state for instant slider feedback; synced to store via debounce
  const [local, setLocal] = useState(value);

  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Reset TURNS THE FILTER OFF: the full range is the inactive state, so the
  // slider stops excluding anything and its badge drops to zero. It does NOT
  // restore `defaultRange` — that is still a narrowed, active range (the price
  // default is a $5 floor), so resetting to it left the filter excluding names
  // and looking broken.
  //
  // Reset inherits the removed "X" outright: it clears the filter to its full,
  // inactive range AND collapses the histogram back to "Set Range". One
  // labelled control now does what an unlabelled icon used to.
  const handleReset = () => {
    // Reset to the fully-inactive span [0, UNCAPPED], NOT [min, UNCAPPED]:
    // `min` is the chart's DISPLAY floor (Math.floor of the distribution's
    // min), which can sit ABOVE the securities' true minimum — the History
    // axis starts higher than the youngest listing. Resetting to that floor
    // left `isActive` true (current[0] > dataRange[0]), so a collapsed filter
    // kept excluding (e.g. Listing History showing "455 excluded" at rest).
    // 0 is the store's own inactive floor, so it can never read as a filter.
    const cleared: [number, number] = [0, UNCAPPED];
    setLocal(cleared);
    // Beat any in-flight debounce from a drag that just ended, or it would
    // land after this and silently re-apply the old range.
    if (timerRef.current) clearTimeout(timerRef.current);
    onChange(cleared);
    setExpanded(false);
  };
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from store → local when store value changes (e.g. reset, PillInput commit)
  useEffect(() => {
    setLocal((prev) =>
      prev[0] === value[0] && prev[1] === value[1] ? prev : value,
    );
  }, [value]);

  // When editing an existing strategy the store hydrates AFTER mount, so an
  // active value can arrive while the filter is still collapsed — auto-open then.
  // Only ever OPENS, and only until the user's own choice is recorded, so it
  // never fights someone who cleared a filter and left it open.
  useEffect(() => {
    if (valueActive && storedExpanded == null) setExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueActive, storedExpanded]);

  // Clear pending debounce on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Clearing a bound with its "x" removes ONLY that bound and keeps the card
  // OPEN: the freed side turns back into its "+ Min" / "+ Max" button so it can
  // be re-added in place. It deliberately no longer collapses — collapsing on
  // clear is exactly what made a cleared bound feel unrecoverable (the card
  // vanished behind "Set Range"), and it also closed the card when the MAX was
  // cleared while a below-floor min still stood (that min tested as "neutral"
  // against the raw floor). Reset stays the one action that turns the filter
  // off AND collapses it.
  const clearBound = (next: [number, number]) => {
    setLocal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    onChange(next);
  };

  const handleSliderChange = (v: [number, number]) => {
    // Reaching the field's PHYSICAL ceiling (valueMax, e.g. 100% for Float) IS
    // "no cap" — the same rule the adapter applies on submit (toRange treats a
    // max at/above the natural ceiling as null). Checked first, unconditionally,
    // so dragging the max thumb all the way right — from ANY starting value,
    // not only an already-uncapped one — resolves to the UNCAPPED sentinel
    // instead of a concrete ceiling number that would read as an active filter
    // forever (isAtMax/valueActive only recognize the sentinel).
    if (valueMax != null && v[1] >= valueMax) {
      v = [v[0], UNCAPPED];
    } else if (local[1] > max && v[1] >= max * 0.999) {
      // Radix clamps to the slider's own domain, so dragging the MIN thumb on a
      // trimmed chart would quietly pull an uncapped max down to the chart's
      // ceiling. Keep the uncapped max unless the user actually moved it.
      // …and the log slider resolves through pow/log10, so the ceiling comes
      // back a hair off rather than exactly `max`.
      v = [v[0], local[1]];
    }
    setLocal(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChangeRef.current(v), DEBOUNCE_MS);
  };

  // "No cap" (the "+ Max" button) is the UNCAPPED sentinel ONLY — a cleared or
  // defaulted max. Any finite value is a real cap and shows its number, even one
  // above the visible chart: the chart trims the top 1%, and a point-in-time
  // backtest may cap above today's max, so the user must be able to type it
  // flexibly rather than have it collapse to "+ Max".
  const isAtMax = local[1] >= UNCAPPED;

  // The min is "set" — and must show its literal value — whenever it's a real
  // positive floor that isn't the cleared/at-floor state. A floor BELOW the
  // chart's minimum (e.g. a $5 price floor on a universe that starts at ~$20) is
  // a genuine, config-set value and is displayed as-is rather than blanked to
  // "+ Min". The cleared state (`local[0] === min`) and the unset default
  // (`<= 0`) still render the "+ Min" button.
  const minIsSet = local[0] > 0 && local[0] !== min;

  return (
    <div className="space-y-1">
      {/* Header — the filter's name and what it costs sit left; the controls
          (Reset, Lin/Log) sit right, matching the Figma
          (UniverseSelection.tsx:3818). The toggle shows whenever the filter
          has a usable (positive) log distribution (hasLogData). */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Same treatment as the sibling filter headers on this step
              ("Regions, Countries, & Exchanges", SecurityTypeClassification):
              15px rather than text-sm's 14, and truncating so a long label
              can't push the Reset / scale controls off the row. */}
          <span className="text-[15px] font-medium text-muted-foreground truncate">
            {label}
          </span>
          {excludedCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-md border border-destructive/20 bg-destructive/5 text-destructive/80 font-medium whitespace-nowrap">
              {excludedCount.toLocaleString()} excluded
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Reset shows on every OPEN histogram (not just active ones) so the
            control is consistent across filters — on an already-full range it
            simply clears the min and collapses. */}
        {expanded && (
          <FilterResetButton onClick={handleReset} />
        )}
        {expanded && hasLogData && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-muted-foreground">Scale:</span>
            <div className="flex rounded border border-border/50 overflow-hidden">
              {(['linear', 'log'] as const).map((scale) => {
                const active = (scale === 'log') === isLogScale;
                return (
                  <button
                    key={scale}
                    type="button"
                    onClick={() => setIsLogScale(scale === 'log')}
                    className={`px-2 py-1 text-xs transition-colors ${
                      active
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-400'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {scale === 'linear' ? 'Lin' : 'Log'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>

      {!expanded ? (
        <button
          type="button"
          onClick={() => {
            // Opening the filter seeds the product-default range (e.g. a
            // minimum threshold) so it isn't a no-op full-range selection.
            if (defaultRange) onChange(defaultRange);
            setExpanded(true);
          }}
          className="flex items-center gap-2 w-full h-8 px-3 justify-start rounded-md border border-dashed border-primary/30 bg-transparent text-sm text-foreground hover:bg-muted/50 transition-colors"
        >
          <Plus className="w-3 h-3" />
          Set Range
        </button>
      ) : (
        // Chart, slider, axis AND the two bound fields live in ONE card. The
        // fields used to sit outside it, which read as a separate control
        // rather than as the numeric half of the range the slider sets.
        <div className="rounded-lg border border-border/40 bg-card/40 backdrop-blur-sm shadow-sm px-3 pt-3 pb-3 relative">
            <HistogramBars
              distribution={activeDist}
              min={activeMin}
              max={activeMax}
              rangeMin={local[0]}
              rangeMax={local[1]}
              isLog={showLog}
            />

            <DualSlider
              min={min}
              max={max}
              step={step}
              value={local}
              onChange={handleSliderChange}
              logBounds={showLog ? [activeMin, activeMax] : undefined}
            />

            <AxisLabels
              min={activeMin}
              max={activeMax}
              ticks={axisTicks}
              formatAxis={formatAxis}
              isLog={showLog}
            />

            <div className="flex items-end gap-2 pt-1 mt-1 border-t border-border/30">
            <PillInput
              label={minLabel}
              value={local[0]}
              formatDisplay={(v) => (minIsSet ? formatInput(v) : '')}
              parseInput={parseInput}
              placeholder="Min"
              isButton={!minIsSet}
              buttonLabel="Min"
              // "+ Min" restores the default floor in one click (mirror of
              // "+ Max" → p90), then shows it as an editable value. Omitted →
              // opens an empty field to type a floor into.
              onButtonClick={
                minSeed != null
                  ? () => {
                      // Restore EXACTLY the Set-Range floor (defaultRange[0]) —
                      // same value, one click. Keep the current cap; only if
                      // the floor would cross it do we open the cap (as Set
                      // Range itself does), so "+ Min" always seeds the default
                      // floor and the range can never invert.
                      const flooredMin = Math.round(minSeed * 100) / 100;
                      const cap =
                        flooredMin > local[1]
                          ? (defaultRange?.[1] ?? dataMax)
                          : local[1];
                      const next: [number, number] = [flooredMin, cap];
                      setLocal(next);
                      onChange(next);
                    }
                  : undefined
              }
              onClear={() => clearBound([min, local[1]])}
              onCommit={(num) => {
                // Flexible input: bounded only by ≥ 0, ≤ the current cap, and ≤
                // valueMax (a field's PHYSICAL ceiling, e.g. 100% for Float —
                // NOT the chart's snapshot max). A $2 / $5 floor on a
                // higher-starting universe stays a valid point-in-time value.
                const clamped = Math.max(
                  0,
                  Math.min(num, local[1], valueMax ?? UNCAPPED),
                );
                onChange([clamped, local[1]]);
              }}
            />
            <span
              className="text-muted-foreground/40 pb-2.5 text-center"
              style={{ minWidth: '48px' }}
            >
              &#8212;
            </span>
            {maxAvailable ? (
              <PillInput
                label={maxLabel}
                value={local[1]}
                formatDisplay={(v) => (isAtMax ? '' : formatInput(v))}
                parseInput={parseInput}
                placeholder={maxPlaceholder}
                isButton={isAtMax}
                buttonLabel="Max"
                // "+ Max" seeds the 90th-percentile cap in one click (drops the
                // top ~10% of extreme-high values), then shows it as a real,
                // editable value. Omitted → opens an empty field as before.
                onButtonClick={
                  maxSeed != null
                    ? () => {
                        // Round to 2dp — p90 comes off raw prices like
                        // 483.5799…, which would seed an ugly cap.
                        const capped = Math.max(local[0], maxSeed);
                        const next: [number, number] = [
                          local[0],
                          Math.round(capped * 100) / 100,
                        ];
                        setLocal(next);
                        onChange(next);
                      }
                    : undefined
                }
                // Clear → unbounded (above the ceiling) so it reverts to "+ Max".
                onClear={() =>
                  clearBound([local[0], Number.MAX_SAFE_INTEGER])
                }
                onCommit={(num) => {
                  // Reaching the PHYSICAL ceiling (valueMax, e.g. 100% for
                  // Float) by TYPING must resolve to "no cap" too — the same
                  // rule handleSliderChange applies on drag, and the same rule
                  // the adapter applies on submit. Without this, typing
                  // exactly 100 stored a concrete 100 (not the sentinel), and
                  // isAtMax/valueActive — which only recognize the sentinel —
                  // read the field as permanently active.
                  if (valueMax != null && num >= valueMax) {
                    onChange([local[0], UNCAPPED]);
                    return;
                  }
                  // Otherwise flexible: bounded only by ≥ the current floor
                  // and ≤ valueMax. NOT clamped to the chart max — a cap may
                  // sit above today's (trimmed) max for a point-in-time run.
                  const clamped = Math.max(
                    local[0],
                    Math.min(num, valueMax ?? UNCAPPED),
                  );
                  onChange([local[0], clamped]);
                }}
              />
            ) : (
              // Max not available (e.g. Listing History) — disabled placeholder,
              // matching the Figma's greyed, non-interactive "+ Max" field.
              <div className="flex-1 space-y-1.5">
                <p className="text-xs text-muted-foreground">{maxLabel}</p>
                <div className="flex h-9 w-full cursor-not-allowed items-center justify-center gap-1 rounded-md border border-border/30 bg-muted/30 px-3 text-sm font-medium text-muted-foreground opacity-50 shadow-sm">
                  <Plus className="w-3 h-3" />
                  Max
                </div>
                </div>
              )}
            </div>
        </div>
      )}
    </div>
  );
}
